import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withState, withMethods, patchState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, tap, exhaustMap } from 'rxjs';

import { AccessService } from '@app/core-interactions/services/access.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications.service';
import { AccountType } from '@app/stores/models/app/account-type';
import { ClientAccess } from '@core-interactions/models/client-access';
import { Module } from '@core-interactions/models/module';
import { ClientPowerBIReport } from '@power-bi/models/client-power-bi-report';

export interface ModulesState {
  entities: Module[];
  loaded: boolean;
}

export interface ClientsState {
  entities: ClientAccess[];
  loading: boolean;
  loaded: boolean;
}

export interface ReportsState {
  entities: ClientPowerBIReport[];
  loading: boolean;
  loaded: boolean;
  error: HttpErrorResponse | null;
}

export interface AccessState {
  modules: ModulesState;
  clients: ClientsState;
  reports: ReportsState;
}

export const INITIAL_STATE: AccessState = {
  modules: { entities: [], loaded: false },
  clients: { entities: [], loading: false, loaded: false },
  reports: { entities: [], loading: false, loaded: false, error: null }
};

export const ACCESS_STORE = signalStore(
  withState<AccessState>(INITIAL_STATE),
  withMethods(
    (
      store,
      accessService = inject(AccessService),
      toast = inject(ToastNotificationsService)
    ) => ({
      loadModules: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, {
              modules: { entities: [], loaded: false }
            })
          ),
          exhaustMap(() =>
            accessService.loadModules().pipe(
              tapResponse({
                next: modules =>
                  patchState(store, {
                    modules: { entities: modules, loaded: true }
                  }),
                error: () => {
                  toast.error('Loading modules failed');

                  patchState(store, {
                    modules: { entities: [], loaded: true }
                  });
                }
              })
            )
          )
        )
      ),

      loadClients: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, {
              clients: { entities: [], loading: true, loaded: false }
            })
          ),
          exhaustMap(() =>
            accessService.loadClients().pipe(
              tapResponse({
                next: clients =>
                  patchState(store, {
                    clients: { entities: clients, loading: false, loaded: true }
                  }),
                error: () => {
                  toast.error('Loading clients failed');
                  patchState(store, {
                    clients: { entities: [], loading: false, loaded: true }
                  });
                }
              })
            )
          )
        )
      ),

      loadReports: rxMethod<AccountType>(
        pipe(
          tap(() =>
            patchState(store, {
              reports: {
                entities: [],
                loading: true,
                loaded: false,
                error: null
              }
            })
          ),
          exhaustMap(accountType =>
            accessService.loadReports(accountType).pipe(
              tapResponse({
                next: reports =>
                  patchState(store, {
                    reports: {
                      entities: reports,
                      loading: false,
                      loaded: true,
                      error: null
                    }
                  }),
                error: (err: unknown) => {
                  toast.error('Loading dashboards failed');

                  const httpError =
                    err instanceof HttpErrorResponse ? err : null;
                  patchState(store, {
                    reports: {
                      entities: [],
                      loading: false,
                      loaded: true,
                      error: httpError
                    }
                  });
                }
              })
            )
          )
        )
      ),

      resetReports(): void {
        patchState(store, {
          reports: { entities: [], loading: false, loaded: false, error: null }
        });
      }
    })
  )
);
